/**
 * 工作室能力评估数据常量
 * 数据来源：Figma 设计稿
 */

import { IStudioCapability, IStudioTypeConfig, TStudioType } from '../types';

/**
 * 工作室类型配置
 * 用于图例显示
 */
export const STUDIO_TYPE_CONFIG: IStudioTypeConfig[] = [
  {
    type: 'Survival',
    color: '#F17C86', // 红色系 - 生存型
    borderColor: 'rgba(241, 124, 134, 0.3)',
    label: 'Survival',
    data: [
      { xAxis: 0, yAxis: 0 },
      { xAxis: 60, yAxis: 60 },
    ],
    markAreaColorStops: [
      { offset: 0, color: 'rgba(241, 124, 134, 0.25)' },
      { offset: 1, color: 'rgba(241, 124, 134, 0.05)' },
    ],
  },
  {
    type: 'Growing',
    color: '#5086F3', // 深蓝色系 - 常青型
    borderColor: 'rgba(80, 134, 243, 0.3)',
    label: 'Growing',
    data: [
      { xAxis: 60, yAxis: 60 },
      { xAxis: 90, yAxis: 90 },
    ],
    markAreaColorStops: [
      { offset: 0, color: 'rgba(80, 134, 243, 0.25)' },
      { offset: 1, color: 'rgba(80, 134, 243, 0.05)' },
    ],
  },
  {
    type: 'Evergreen',
    color: '#69D19C', // 绿色系 - 成长型
    borderColor: 'rgba(105, 209, 156, 0.3)', // #69D19C at 30%
    label: 'Evergreen',
    data: [
      { xAxis: 90, yAxis: 90 },
      { xAxis: 100, yAxis: 100 },
    ],
    markAreaColorStops: [
      { offset: 0, color: 'rgba(105, 209, 156, 0.3)' }, // #69D19C at 30%
      { offset: 1, color: 'rgba(105, 209, 156, 0.05)' }, // #69D19C at 5%
    ],
  },
  {
    type: 'studio',
    color: '#BBBDCB', // 灰色系 - 其他工作室
    borderColor: 'rgba(187, 189, 203, 0.3)',
    label: 'Others',
    data: [],
    showInLegend: false,
  },
];

/**
 * 工作室能力评估数据
 * 坐标系：Quality (X轴: 0-100), Efficiency (Y轴: 0-100)
 * 数据从 Figma 设计稿中提取
 */
export const STUDIO_CAPABILITY_DATA: IStudioCapability[] = [
  // Growing 类型 - 成长型工作室（中等质量中等效率）
  {
    name: 'DE',
    quality: 70,
    efficiency: 72,
    type: 'Growing',
  },
  {
    name: 'TL',
    quality: 85,
    efficiency: 57,
    type: 'studio',
  },
  {
    name: 'Arrowhead',
    quality: 70,
    efficiency: 85,
    type: 'Growing',
  },
  // Survival 类型 - 生存型工作室（低质量低效率）
  {
    name: 'Yager',
    quality: 40,
    efficiency: 50,
    type: 'Survival',
  },
  {
    name: 'Lighthouse',
    quality: 40,
    efficiency: 57,
    type: 'Survival',
  },
  {
    name: 'Shiftup',
    quality: 45,
    efficiency: 62,
    type: 'Survival',
    sub_studio: 'Yongxing',
    icon: 'Yongxing',
  },
  {
    name: 'Inflexion',
    quality: 25,
    efficiency: 45,
    type: 'Survival',
  },
  {
    name: 'Sharkmob',
    quality: 45,
    efficiency: 54,
    type: 'Survival',
  },
  {
    name: '10C',
    quality: 30,
    efficiency: 49,
    type: 'Survival',
  },
  {
    name: 'Sumo',
    quality: 20,
    efficiency: 35,
    type: 'Survival',
  },
  {
    name: 'Joy Workshop',
    quality: 32,
    efficiency: 42,
    type: 'Survival',
  },
  // Studio 类型 - 其他工作室（不在上述范围内）
  {
    name: 'MC',
    quality: 92,
    efficiency: 92,
    type: 'Evergreen',
  },
  {
    name: 'Mundfish',
    quality: 70,
    efficiency: 78,
    type: 'Growing',
  },
  {
    name: 'Platinum',
    quality: 55,
    efficiency: 58,
    type: 'studio',
  },
  {
    name: 'Fatshark',
    quality: 65,
    efficiency: 63,
    type: 'Growing',
  },
  {
    name: 'Stunlock',
    quality: 70,
    efficiency: 66,
    type: 'Growing',
  },
  {
    name: 'Funcom',
    quality: 65,
    efficiency: 58,
    type: 'studio',
  },
  {
    name: 'TR',
    quality: 50,
    efficiency: 51,
    type: 'Survival',
  },
  {
    name: 'GGG',
    quality: 75,
    efficiency: 54,
    type: 'studio',
  },
  {
    name: 'Crytek',
    quality: 50,
    efficiency: 47,
    type: 'Survival',
  },
];

/**
 * 根据类型获取工作室数据
 */
export const getStudiosByType = (type: TStudioType): IStudioCapability[] => {
  return STUDIO_CAPABILITY_DATA.filter(studio => studio.type === type);
};

/**
 * 根据类型获取颜色
 */
export const getColorByType = (type: TStudioType): string => {
  const config = STUDIO_TYPE_CONFIG.find(item => item.type === type);
  return config?.color || '#5086F3';
};
